import type { Dispatch, SetStateAction } from 'react'
import { ChevronDown, ChevronUp, Copy, Plus, Trash2 } from 'lucide-react'
import { makeId } from '../defaults'
import {
  calculateAnalyticalDesign,
  calculatePanelMetrics,
  calculateReducedGap,
  calculateSourceGeometry,
  syncLpiFromPitch,
  syncPitchFromLpi,
  validateThickGeometry,
} from '../physics'
import { validateNominalPhysicalInputs } from '../simulation'
import type { AppState, OpticalLayer } from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { Field, Metric, ModelBadge, Notice, NumberField, Section, SelectField, formatNumber } from './ui'

interface SetupTabProps {
  state: AppState
  setState: Dispatch<SetStateAction<AppState>>
  onClearExample: () => void
  onLoadExample: () => void
}

export function SetupTab({ state, setState, onClearExample, onLoadExample }: SetupTabProps) {
  const panel = calculatePanelMetrics(state)
  const source = calculateSourceGeometry(state)
  const gap = calculateReducedGap(state.layers)
  const design = calculateAnalyticalDesign(state)
  const geometry = validateThickGeometry(
    state.lens.thick.pitchMm,
    state.lens.thick.radiusMm,
    state.lens.thick.centerThicknessMm,
  )
  const inputErrors = validateNominalPhysicalInputs(state)
  const viewingRangeConsumed =
    state.view.criteria.enabled.viewingRange ||
    state.toleranceSettings.includeCriteria.viewingRange
  const distanceRangeIsInverted =
    viewingRangeConsumed &&
    state.view.distanceMinMm !== null &&
    state.view.distanceMaxMm !== null &&
    Number.isFinite(state.view.distanceMinMm) &&
    Number.isFinite(state.view.distanceMaxMm) &&
    state.view.distanceMinMm > state.view.distanceMaxMm
  const distanceMinimumError =
    viewingRangeConsumed &&
    (state.view.distanceMinMm === null ||
      !Number.isFinite(state.view.distanceMinMm) ||
      state.view.distanceMinMm <= 0)
      ? 'Minimum must be finite and greater than 0 when enabled.'
      : distanceRangeIsInverted
        ? 'Minimum must be less than or equal to maximum.'
        : undefined
  const distanceMaximumError =
    viewingRangeConsumed &&
    (state.view.distanceMaxMm === null ||
      !Number.isFinite(state.view.distanceMaxMm) ||
      state.view.distanceMaxMm <= 0)
      ? 'Maximum must be finite and greater than 0 when enabled.'
      : distanceRangeIsInverted
        ? 'Maximum must be greater than or equal to minimum.'
        : undefined

  const criterionError = (
    enabled: boolean,
    value: number,
  ): string | undefined => {
    if (!enabled || (Number.isFinite(value) && value >= 0)) {
      return undefined
    }
    return 'Must be finite and at least 0 when enabled.'
  }

  const updateLcd = <K extends keyof AppState['lcd']>(key: K, value: AppState['lcd'][K]) =>
    setState((current) => ({ ...current, lcd: { ...current.lcd, [key]: value } }))

  const updateView = <K extends keyof AppState['view']>(key: K, value: AppState['view'][K]) =>
    setState((current) => ({ ...current, view: { ...current.view, [key]: value } }))

  const updateLayer = (id: string, patch: Partial<OpticalLayer>) =>
    setState((current) => ({
      ...current,
      layers: current.layers.map((layer) => (layer.id === id ? { ...layer, ...patch } : layer)),
    }))

  const moveLayer = (index: number, direction: -1 | 1) =>
    setState((current) => {
      const next = [...current.layers]
      const target = index + direction
      if (target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target], next[index]]
      return { ...current, layers: next }
    })

  const addLayer = () =>
    setState((current) => ({
      ...current,
      layers: [
        ...current.layers,
        {
          id: makeId('layer'),
          name: 'New layer',
          thicknessMm: null,
          refractiveIndex: null,
          enabled: true,
          thicknessToleranceMm: null,
          indexTolerance: null,
          distribution: 'normal',
          notes: '',
        },
      ],
    }))

  return (
    <div className="tab-stack">
      <h1 className="sr-only">Setup</h1>
      {state.exampleLoaded ? (
        <Notice tone="warning" title="Illustrative values are loaded">
          These values exercise the workbench. They are not typical defaults and must be replaced with measured or documented panel, stack, and lens data.{' '}
          <button className="text-button" onClick={onClearExample}>Clear example values</button>
        </Notice>
      ) : (
        <Notice title="Blank project">
          Missing inputs remain missing and block dependent results.{' '}
          <button className="text-button" onClick={onLoadExample}>Load the worked example</button>
        </Notice>
      )}

      <Section
        title="LCD geometry"
        description="The source lattice is derived from explicit panel geometry and routing mode."
      >
        <div className="form-grid four">
          <NumberField
            label="Active width"
            unit="mm"
            value={state.lcd.activeWidthMm}
            onChange={(value) => updateLcd('activeWidthMm', value)}
            min={0}
            error={inputErrors['lcd.activeWidthMm']}
          />
          <NumberField
            label="Active height"
            unit="mm"
            value={state.lcd.activeHeightMm}
            onChange={(value) => updateLcd('activeHeightMm', value)}
            min={0}
            error={inputErrors['lcd.activeHeightMm']}
          />
          <NumberField
            label="Horizontal resolution"
            unit="px"
            value={state.lcd.resolutionX}
            onChange={(value) => updateLcd('resolutionX', value)}
            min={1}
            step={1}
            error={inputErrors['lcd.resolutionX']}
          />
          <NumberField
            label="Vertical resolution"
            unit="px"
            value={state.lcd.resolutionY}
            onChange={(value) => updateLcd('resolutionY', value)}
            min={1}
            step={1}
            error={inputErrors['lcd.resolutionY']}
          />
        </div>
        <div className="metric-strip">
          <Metric label="Horizontal pitch" value={formatNumber(panel.pixelPitchXmm, 6)} unit="mm" />
          <Metric label="Vertical pitch" value={formatNumber(panel.pixelPitchYmm, 6)} unit="mm" />
          <Metric label="Pixel density" value={formatNumber(panel.ppi, 2)} unit="PPI" />
        </div>

        <div className="subsection-heading">
          <h3>Source lattice</h3>
          <span>Choose the real routed source cell, not a presumed RGB layout.</span>
        </div>
        <div className="form-grid three">
          <SelectField
            label="Lattice mode"
            value={state.lcd.sourceMode}
            onChange={(value) => updateLcd('sourceMode', value as AppState['lcd']['sourceMode'])}
            options={[
              { value: 'pixel-columns', label: 'Full RGB pixel columns' },
              { value: 'rgb-stripe', label: 'Vertical RGB-stripe subpixels (advanced)' },
              { value: 'custom', label: 'Custom source pitch' },
            ]}
          />
          {state.lcd.sourceMode === 'rgb-stripe' ? (
            <NumberField
              label="Horizontal subpixels"
              unit="per pixel"
              value={state.lcd.horizontalSubpixels}
              onChange={(value) => updateLcd('horizontalSubpixels', value)}
              min={1}
              step={1}
              error={inputErrors['lcd.horizontalSubpixels']}
            />
          ) : state.lcd.sourceMode === 'custom' ? (
            <NumberField
              label="Custom source pitch"
              unit="mm"
              value={state.lcd.customSourcePitchMm}
              onChange={(value) => updateLcd('customSourcePitchMm', value)}
              min={0}
              error={inputErrors['lcd.customSourcePitchMm']}
            />
          ) : (
            <NumberField label="Source pitch" unit="mm" value={source.sourcePitchMm} readOnly />
          )}
          <NumberField label="Repeating source width G" unit="mm" value={source.repeatWidthMm} readOnly />
        </div>
        {state.lcd.sourceMode === 'rgb-stripe' ? (
          <Notice tone="warning">
            Advanced mode: view assignment and color sampling are coupled. The v1 model treats the entered stripe pitch as a one-dimensional source lattice and does not infer panel subpixel order.
          </Notice>
        ) : null}

        <div className="form-grid four">
          <SelectField
            label="Aperture entry"
            value={state.lcd.apertureMode}
            onChange={(value) => updateLcd('apertureMode', value as 'width' | 'fill-factor')}
            options={[
              { value: 'fill-factor', label: 'Source fill factor' },
              { value: 'width', label: 'Source aperture width' },
            ]}
          />
          {state.lcd.apertureMode === 'fill-factor' ? (
            <NumberField
              label="Source fill factor"
              unit="0–1"
              value={state.lcd.sourceFillFactor}
              onChange={(value) => updateLcd('sourceFillFactor', value)}
              min={0}
              max={1}
              error={inputErrors['lcd.sourceFillFactor']}
            />
          ) : (
            <NumberField
              label="Source aperture"
              unit="mm"
              value={state.lcd.sourceApertureMm}
              onChange={(value) => updateLcd('sourceApertureMm', value)}
              min={0}
              error={inputErrors['lcd.sourceApertureMm']}
            />
          )}
          <NumberField
            label="Black-matrix width"
            unit="mm, optional"
            value={state.lcd.blackMatrixWidthMm}
            onChange={(value) => updateLcd('blackMatrixWidthMm', value)}
            min={0}
          />
          <NumberField label="Resolved aperture" unit="mm" value={source.sourceApertureMm} readOnly />
        </div>

        <details className="advanced-panel">
          <summary>Angular emission and spectral indices</summary>
          <div className="form-grid three">
            <SelectField
              label="Angular emission"
              value={state.lcd.emissionModel}
              onChange={(value) => updateLcd('emissionModel', value as AppState['lcd']['emissionModel'])}
              options={[
                { value: 'flat', label: 'Flat angular weight' },
                { value: 'lambertian', label: 'Lambertian cos(θ)' },
                { value: 'gaussian', label: 'Gaussian, entered HWHM' },
              ]}
            />
            {state.lcd.emissionModel === 'gaussian' ? (
              <NumberField
                label="Gaussian half-angle"
                unit="deg HWHM"
                value={state.lcd.gaussianHalfAngleDeg}
                onChange={(value) => updateLcd('gaussianHalfAngleDeg', value)}
                min={0}
                max={89}
              />
            ) : null}
          </div>
          <div className="spectral-grid">
            {(['red', 'green', 'blue'] as const).map((channel) => (
              <div key={channel} className="spectral-row">
                <strong>{channel[0].toUpperCase() + channel.slice(1)}</strong>
                <NumberField
                  label="Wavelength"
                  unit="nm"
                  value={state.lcd.rgb[channel].wavelengthNm}
                  onChange={(value) =>
                    setState((current) => ({
                      ...current,
                      lcd: {
                        ...current.lcd,
                        rgb: {
                          ...current.lcd.rgb,
                          [channel]: { ...current.lcd.rgb[channel], wavelengthNm: value },
                        },
                      },
                    }))
                  }
                />
                <NumberField
                  label="Lens index n(λ)"
                  value={state.lcd.rgb[channel].refractiveIndex}
                  onChange={(value) =>
                    setState((current) => ({
                      ...current,
                      lcd: {
                        ...current.lcd,
                        rgb: {
                          ...current.lcd.rgb,
                          [channel]: { ...current.lcd.rgb[channel], refractiveIndex: value },
                        },
                      },
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </details>
      </Section>

      <Section
        title="Optical stack"
        description={
          state.lens.mode === 'thin'
            ? 'Enabled layers terminate at the ideal thin-lens plane.'
            : 'Enabled layers terminate at the rear flat lens surface. Lens thickness is traced separately.'
        }
        actions={
          <button className="secondary-button" onClick={addLayer}>
            <Plus size={15} aria-hidden="true" /> Add layer
          </button>
        }
      >
        <HorizontalScrollRegion className="table-scroll" label="Optical stack layers">
          <table className="data-table layer-table">
            <thead>
              <tr>
                <th>On</th>
                <th>Layer</th>
                <th>Thickness (mm)</th>
                <th>Index n</th>
                <th>Thickness σ / ± (mm)</th>
                <th>Index σ / ±</th>
                <th>Distribution</th>
                <th>Notes</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {state.layers.map((layer, index) => {
                const thicknessError = inputErrors[`layers.${layer.id}.thicknessMm`]
                const indexError = inputErrors[`layers.${layer.id}.refractiveIndex`]
                const errorIdBase = layer.id.replace(/[^a-zA-Z0-9_-]/g, '-')
                const thicknessErrorId = `layer-${errorIdBase}-thickness-error`
                const indexErrorId = `layer-${errorIdBase}-index-error`
                return (
                  <tr key={layer.id} className={layer.enabled ? '' : 'disabled-row'}>
                  <td>
                    <input
                      type="checkbox"
                      checked={layer.enabled}
                      onChange={(event) => updateLayer(layer.id, { enabled: event.target.checked })}
                      aria-label={`Enable ${layer.name}`}
                    />
                  </td>
                  <td>
                    <input
                      value={layer.name}
                      onChange={(event) => updateLayer(layer.id, { name: event.target.value })}
                      aria-label="Layer name"
                    />
                  </td>
                  <td>
                    <input type="number" value={layer.thicknessMm ?? ''} onChange={(event) => updateLayer(layer.id, { thicknessMm: event.target.value === '' ? null : Number(event.target.value) })} aria-label={`${layer.name} thickness`} aria-invalid={thicknessError ? true : undefined} aria-describedby={thicknessError ? thicknessErrorId : undefined} />
                    {thicknessError ? <span id={thicknessErrorId} className="field-error table-field-error" role="alert">{thicknessError}</span> : null}
                  </td>
                  <td>
                    <input type="number" value={layer.refractiveIndex ?? ''} onChange={(event) => updateLayer(layer.id, { refractiveIndex: event.target.value === '' ? null : Number(event.target.value) })} aria-label={`${layer.name} refractive index`} aria-invalid={indexError ? true : undefined} aria-describedby={indexError ? indexErrorId : undefined} />
                    {indexError ? <span id={indexErrorId} className="field-error table-field-error" role="alert">{indexError}</span> : null}
                  </td>
                  <td><input type="number" value={layer.thicknessToleranceMm ?? ''} onChange={(event) => updateLayer(layer.id, { thicknessToleranceMm: event.target.value === '' ? null : Number(event.target.value) })} aria-label={`${layer.name} thickness tolerance`} /></td>
                  <td><input type="number" value={layer.indexTolerance ?? ''} onChange={(event) => updateLayer(layer.id, { indexTolerance: event.target.value === '' ? null : Number(event.target.value) })} aria-label={`${layer.name} index tolerance`} /></td>
                  <td>
                    <select value={layer.distribution} onChange={(event) => updateLayer(layer.id, { distribution: event.target.value as OpticalLayer['distribution'] })} aria-label={`${layer.name} tolerance distribution`}>
                      <option value="normal">Normal σ</option>
                      <option value="uniform">Uniform ±</option>
                    </select>
                  </td>
                  <td><input value={layer.notes} onChange={(event) => updateLayer(layer.id, { notes: event.target.value })} aria-label={`${layer.name} notes`} /></td>
                  <td>
                    <div className="row-actions">
                      <button className="icon-button" onClick={() => moveLayer(index, -1)} disabled={index === 0} aria-label={`Move ${layer.name} up`} title="Move up"><ChevronUp size={15} /></button>
                      <button className="icon-button" onClick={() => moveLayer(index, 1)} disabled={index === state.layers.length - 1} aria-label={`Move ${layer.name} down`} title="Move down"><ChevronDown size={15} /></button>
                      <button className="icon-button" onClick={() => setState((current) => ({ ...current, layers: [...current.layers.slice(0, index + 1), { ...layer, id: makeId('layer'), name: `${layer.name} copy` }, ...current.layers.slice(index + 1)] }))} aria-label={`Duplicate ${layer.name}`} title="Duplicate"><Copy size={15} /></button>
                      <button className="icon-button danger" onClick={() => setState((current) => ({ ...current, layers: current.layers.filter((item) => item.id !== layer.id) }))} aria-label={`Delete ${layer.name}`} title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </HorizontalScrollRegion>
        {inputErrors.layers ? <Notice tone="danger">{inputErrors.layers}</Notice> : null}
        {!state.layers.length ? <Notice tone="warning">Add at least one measured layer. A zero or missing stack is not assumed.</Notice> : null}
        <div className="metric-strip">
          <Metric label="Enabled layers" value={gap.usedLayers} />
          <Metric label="Physical thickness" value={formatNumber(gap.physicalThicknessMm, 4)} unit="mm" />
          <Metric label="Reduced optical gap d" value={formatNumber(gap.reducedGapMm, 6)} unit="mm" note="Σ tᵢ/nᵢ" />
        </div>
      </Section>

      <Section title="View design" description="Target geometry and explicit acceptance criteria remain part of every status decision.">
        <div className="form-grid four">
          <NumberField label="Number of views V" value={state.view.views} onChange={(value) => updateView('views', Math.max(2, Math.round(value ?? 2)))} min={2} max={32} step={1} error={inputErrors['view.views']} />
          <NumberField label="Target viewing distance" unit="mm" value={state.view.targetDistanceMm} onChange={(value) => updateView('targetDistanceMm', value)} min={0} error={inputErrors['view.targetDistanceMm']} />
          <NumberField
            label="Distance range minimum"
            unit="mm"
            value={state.view.distanceMinMm}
            onChange={(value) => updateView('distanceMinMm', value)}
            min={0}
            error={distanceMinimumError}
          />
          <NumberField
            label="Distance range maximum"
            unit="mm"
            value={state.view.distanceMaxMm}
            onChange={(value) => updateView('distanceMaxMm', value)}
            min={0}
            error={distanceMaximumError}
          />
          <NumberField label="Adjacent zone spacing" unit="mm" value={state.view.targetSpacingMm} onChange={(value) => updateView('targetSpacingMm', value)} min={0} error={inputErrors['view.targetSpacingMm']} />
          <NumberField label="Eye separation" unit="mm" value={state.view.eyeSeparationMm} onChange={(value) => updateView('eyeSeparationMm', value)} min={0} hint="Used by two-view eye placement." />
          <NumberField label="Pupil / camera aperture" unit="mm" value={state.view.pupilDiameterMm} onChange={(value) => updateView('pupilDiameterMm', value)} min={0} error={inputErrors['view.pupilDiameterMm']} />
          <NumberField label="Head-box width" unit="mm" value={state.view.headBoxWidthMm} onChange={(value) => updateView('headBoxWidthMm', value)} min={0} />
        </div>
        <div className="form-grid two">
          <SelectField
            label="Target center placement"
            value={state.view.centerMode}
            onChange={(value) => updateView('centerMode', value as AppState['view']['centerMode'])}
            options={[
              { value: 'even', label: 'Evenly spaced' },
              { value: 'two-view-eyes', label: 'Two eyes at −E/2, +E/2' },
              { value: 'custom', label: 'Custom list' },
            ]}
          />
          {state.view.centerMode === 'custom' ? (
            <Field label="Custom centers" unit="mm, comma separated">
              <input
                value={state.view.customCentersMm.join(', ')}
                onChange={(event) =>
                  updateView(
                    'customCentersMm',
                    event.target.value
                      .split(',')
                      .map((value) => Number(value.trim()))
                      .filter(Number.isFinite),
                  )
                }
              />
            </Field>
          ) : null}
        </div>

        <div className="subsection-heading">
          <h3>Selected criteria</h3>
          <span>Defaults are starting assumptions, not industry standards.</span>
        </div>
        <div className="criteria-grid">
          {[
            ['crosstalk', 'Maximum crosstalk', 'fraction', 'maxCrosstalk'],
            ['zoneCenter', 'Maximum zone-center error', 'mm', 'maxZoneCenterErrorMm'],
            ['throughput', 'Minimum relative throughput', 'fraction', 'minRelativeThroughput'],
            ['phaseDrift', 'Maximum accumulated drift', 'source cells', 'maxAccumulatedDriftSourceCells'],
          ].map(([enabledKey, label, unit, valueKey]) => {
            const key = enabledKey as keyof typeof state.view.criteria.enabled
            const viewEnabled = state.view.criteria.enabled[key]
            const consumed =
              viewEnabled ||
              state.toleranceSettings.includeCriteria[key]
            const value = state.view.criteria[valueKey as keyof Omit<AppState['view']['criteria'], 'enabled'>] as number
            return (
              <div className="criterion" key={enabledKey}>
              <input
                type="checkbox"
                checked={viewEnabled}
                onChange={(event) =>
                  setState((current) => ({
                    ...current,
                    view: {
                      ...current.view,
                      criteria: {
                        ...current.view.criteria,
                        enabled: {
                          ...current.view.criteria.enabled,
                          [enabledKey]: event.target.checked,
                        },
                      },
                    },
                  }))
                }
                aria-label={`Enable ${label}`}
              />
              <NumberField
                label={label}
                unit={unit}
                value={value}
                onChange={(value) =>
                  setState((current) => ({
                    ...current,
                    view: {
                      ...current.view,
                      criteria: { ...current.view.criteria, [valueKey]: value ?? 0 },
                    },
                  }))
                }
                min={0}
                error={criterionError(consumed, value)}
              />
              </div>
            )
          })}
          <div className="criterion">
            <input
              type="checkbox"
              checked={state.view.criteria.enabled.viewingRange}
              onChange={(event) => setState((current) => ({ ...current, view: { ...current.view, criteria: { ...current.view.criteria, enabled: { ...current.view.criteria.enabled, viewingRange: event.target.checked } } } }))}
              aria-label="Enable viewing-distance range criterion"
            />
            <span>Viewing distance must remain inside the entered range</span>
          </div>
          <NumberField
            label="Marginal-band multiplier"
            unit="× limit"
            value={state.view.criteria.marginalMultiplier}
            onChange={(value) => setState((current) => ({ ...current, view: { ...current.view, criteria: { ...current.view.criteria, marginalMultiplier: Math.max(1, value ?? 1) } } }))}
            min={1}
            step={0.05}
            hint="For minimum criteria, the marginal floor is limit ÷ multiplier."
          />
        </div>
      </Section>

      <Section title="Lenticular model" description="Thin and thick modes use separate numerical models and reference planes.">
        <div className="segmented-control" role="group" aria-label="Lens model">
          <button className={state.lens.mode === 'thin' ? 'selected' : ''} onClick={() => setState((current) => ({ ...current, lens: { ...current.lens, mode: 'thin' } }))}>Ideal thin lens</button>
          <button className={state.lens.mode === 'thick' ? 'selected' : ''} onClick={() => setState((current) => ({ ...current, lens: { ...current.lens, mode: 'thick' } }))}>Circular plano-convex</button>
        </div>
        {state.lens.mode === 'thin' ? (
          <>
            <div className="inline-check">
              <input type="checkbox" id="use-design-target" checked={state.lens.thin.useDesignTarget} onChange={(event) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, useDesignTarget: event.target.checked } } }))} />
              <label htmlFor="use-design-target">Use the calculated design target for simulation</label>
            </div>
            <div className="form-grid four">
              <NumberField label="Actual pitch" unit="mm" value={state.lens.thin.useDesignTarget ? design.requiredPitchMm : state.lens.thin.pitchMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, pitchMm: value, lpi: syncLpiFromPitch(value) } } }))} disabled={state.lens.thin.useDesignTarget} min={0} error={inputErrors['lens.thin.pitchMm']} />
              <NumberField label="Actual LPI" value={state.lens.thin.useDesignTarget ? design.requiredLpi : state.lens.thin.lpi} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, lpi: value, pitchMm: syncPitchFromLpi(value) } } }))} disabled={state.lens.thin.useDesignTarget} min={0} />
              <NumberField label="Focal length / BFL" unit="mm" value={state.lens.thin.useDesignTarget ? design.requiredFocalLengthMm : state.lens.thin.focalLengthMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, focalLengthMm: value } } }))} disabled={state.lens.thin.useDesignTarget} min={0} error={inputErrors['lens.thin.focalLengthMm']} />
              <SelectField label="Focal reference" value={state.lens.thin.focalReference} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, focalReference: value as AppState['lens']['thin']['focalReference'] } } }))} options={[
                { value: 'effective-at-plane', label: 'EFL at ideal lens plane' },
                { value: 'back-from-plane', label: 'BFL from ideal lens plane' },
                { value: 'vendor-unspecified', label: 'Vendor value, reference unspecified' },
              ]} />
              <NumberField label="Lens fill factor" unit="0–1" value={state.lens.thin.fillFactor} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, fillFactor: value } } }))} min={0} max={1} error={inputErrors['lens.thin.fillFactor']} />
              <NumberField label="Lateral lens phase" unit="mm" value={state.lens.thin.lateralOffsetMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, lateralOffsetMm: value } } }))} />
              <NumberField label="Pitch tolerance" unit="mm σ / ±" value={state.lens.thin.pitchToleranceMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, pitchToleranceMm: value } } }))} min={0} />
              <NumberField label="Focal tolerance" unit="mm σ / ±" value={state.lens.thin.focalToleranceMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, focalToleranceMm: value } } }))} min={0} />
              <NumberField label="Phase-offset tolerance" unit="mm σ / ±" value={state.lens.thin.offsetToleranceMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thin: { ...current.lens.thin, offsetToleranceMm: value } } }))} min={0} />
            </div>
            {!state.lens.thin.useDesignTarget && state.lens.thin.focalReference === 'vendor-unspecified' ? <Notice tone="warning">The focal value will not be used for a viewing-distance prediction until its reference convention is selected.</Notice> : null}
          </>
        ) : (
          <>
            <div className="form-grid four">
              <NumberField label="Lens pitch" unit="mm" value={state.lens.thick.pitchMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, pitchMm: value } } }))} min={0} error={inputErrors['lens.thick.pitchMm']} />
              <NumberField label="Derived LPI" value={syncLpiFromPitch(state.lens.thick.pitchMm)} readOnly />
              <NumberField label="Radius of curvature" unit="mm" value={state.lens.thick.radiusMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, radiusMm: value } } }))} min={0} error={inputErrors['lens.thick.radiusMm']} />
              <NumberField label="Lens refractive index" value={state.lens.thick.refractiveIndex} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, refractiveIndex: value } } }))} min={1} error={inputErrors['lens.thick.refractiveIndex']} />
              <NumberField label="Center thickness" unit="mm" value={state.lens.thick.centerThicknessMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, centerThicknessMm: value } } }))} min={0} error={inputErrors['lens.thick.centerThicknessMm']} />
              <NumberField label="External refractive index" value={state.lens.thick.externalIndex} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, externalIndex: value } } }))} min={1} error={inputErrors['lens.thick.externalIndex']} />
              <NumberField label="Lens fill factor" unit="0–1" value={state.lens.thick.fillFactor} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, fillFactor: value } } }))} min={0} max={1} error={inputErrors['lens.thick.fillFactor']} />
              <NumberField label="Lateral lens phase" unit="mm" value={state.lens.thick.lateralOffsetMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, lateralOffsetMm: value } } }))} />
            </div>
            <div className="metric-strip">
              <Metric label="Circular sag" value={formatNumber(geometry.sagMm, 6)} unit="mm" />
              <Metric label="Edge thickness" value={formatNumber(geometry.edgeThicknessMm, 6)} unit="mm" />
              <Metric label="Geometry" value={geometry.valid ? 'Valid' : 'Invalid or incomplete'} />
            </div>
            {geometry.errors.length ? <Notice tone="danger">{geometry.errors.join(' ')}</Notice> : null}
            <details className="advanced-panel">
              <summary>Thick-lens tolerances</summary>
              <div className="form-grid five">
                {[
                  ['pitchToleranceMm', 'Pitch', 'mm'],
                  ['radiusToleranceMm', 'Radius', 'mm'],
                  ['indexTolerance', 'Index', ''],
                  ['thicknessToleranceMm', 'Thickness', 'mm'],
                  ['offsetToleranceMm', 'Phase offset', 'mm'],
                ].map(([key, label, unit]) => (
                  <NumberField key={key} label={`${label} tolerance`} unit={`${unit} σ / ±`} value={state.lens.thick[key as keyof AppState['lens']['thick']] as number | null} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, thick: { ...current.lens.thick, [key]: value } } }))} min={0} />
                ))}
              </div>
            </details>
          </>
        )}
        <div className="form-grid two compact-top">
          <NumberField label="Source lattice phase" unit="mm" value={state.lens.sourcePhaseMm} onChange={(value) => setState((current) => ({ ...current, lens: { ...current.lens, sourcePhaseMm: value ?? 0 } }))} />
          <div className="model-context"><ModelBadge>{state.lens.mode === 'thin' ? 'paraxial thin-lens simulation' : 'geometric thick-lens ray trace'}</ModelBadge><span>Slanted lenticules are not modeled in v1.</span></div>
        </div>
      </Section>
    </div>
  )
}
